/**
 * email.ts — the canonical email field schemas.
 *
 * Email was the last field concern with no shared validator. Phone, domain and
 * EIN each had one, under the barrel's rule that "there is exactly ONE source of
 * truth for these utilities; do not duplicate the validation logic in apps/api
 * or apps/portal" — email was hand-rolled inline TWENTY-FIVE times instead, with
 * five different messages ('Invalid email address', 'Enter a valid email
 * address', 'Enter a valid email', 'Invalid email', and the zod default), two
 * cap wordings, and four normalization behaviours. Two of those copies had no
 * length cap at all while the columns behind them are `varchar(254)`, so a
 * 260-character address passed validation and threw PG `22001` at the write.
 *
 * The two copies in `packages/crm-client` are deliberately NOT converted: it is a
 * separately published package and importing `@crm98/shared` would point the
 * dependency the wrong way. `apps/api/src/config/env.ts` validates env vars, not
 * a user-entered field, and is out of scope for the same reason.
 *
 * There are two schemas because normalization is a real per-field decision, not
 * an oversight, and collapsing it would silently change stored values:
 *
 *   `emailSchema`            validate + cap, value stored exactly as typed.
 *                            For fields whose value is a record of what someone
 *                            entered — a lead's email, a business contact.
 *   `normalizedEmailSchema`  trim + lowercase, then validate + cap. For fields
 *                            used as an identity or a lookup key, where "Bob@x"
 *                            and "bob@x " must be the same row.
 *
 * Pick by asking whether two spellings of the same address must collide. If they
 * must, the field is an identity: use the normalized one. `businesses.billing_email`
 * is the worked example: it looks like a contact address, but
 * `billing-provisioning.service.ts` dedupes on it, so it is an identity — and its
 * two writers disagreed until this module landed, one normalizing and one not.
 */
import { z } from 'zod';
/** `users.email`, `contacts.email`, `businesses.billing_email`, `locations.email` are all varchar(254). */
export const EMAIL_MAX_LENGTH = 254;
/**
 * The one email message. 'Enter a valid email address' because it is what the
 * login and forgot-password forms already render and what more call sites said
 * than any other wording — this schema unifies the rest onto it rather than
 * inventing a sixth.
 */
const INVALID_EMAIL_MESSAGE = 'Enter a valid email address';
const TOO_LONG_MESSAGE = `Email must be ${EMAIL_MAX_LENGTH} characters or less`;
/**
 * Validate and cap; store exactly what was typed.
 *
 * Use for a field that records an address rather than identifies a row. Callers
 * needing optionality or a clearable empty string compose on top —
 * `emailSchema.optional()`, `emailSchema.or(z.literal(''))` — so this stays one
 * definition rather than a family.
 */
export const emailSchema = z
    .string()
    .email(INVALID_EMAIL_MESSAGE)
    .max(EMAIL_MAX_LENGTH, TOO_LONG_MESSAGE);
/**
 * Trim and lowercase, then validate and cap.
 *
 * Use for identity and lookup fields. Trimming happens BEFORE validation, so a
 * pasted address with a trailing space is accepted and stored clean rather than
 * rejected as malformed.
 */
export const normalizedEmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email(INVALID_EMAIL_MESSAGE)
    .max(EMAIL_MAX_LENGTH, TOO_LONG_MESSAGE);
//# sourceMappingURL=email.js.map